import BusinessEntity, { BusinessEntityField, BusinessEntityFilter, BusinessEntityPaging, BusinessEntityQueryVariables, EntityManager, EntityManagerParameters } from './BusinessEntity';
import { BusinessEntityFieldType } from './Constants';

export class EntityTranslationManager<BT extends BusinessEntityTranslation> extends EntityManager<BT>
{
    constructor(parameters: EntityManagerParameters<BT>)
    {
        super({
            name: parameters.name,
            createEntity: parameters.createEntity,
            fields: [
                { name: 'language', type: BusinessEntityFieldType.VarChar, isRequiredInput: true },
                ...parameters.fields ?? [],
            ],
            graphQlQueryAlias: parameters.graphQlQueryAlias
        });
    }
}

export class BusinessEntityTranslation extends BusinessEntity
{
    /** The language code for this translation. */
    language!: string;
    /** The name of the foreign key for the translated entity (e.g. "blogPost" if this entity is a BlogPostTranslation) */
    translatedEntityForeignKeyName!: string;

    // Override: skip the translatedEntityForeignKeyName field when serializing to JSON
    toJson()
    {
        return super.toJson([this.translatedEntityForeignKeyName]);
    }
}

export class BusinessEntityWithTranslation<BT extends BusinessEntityTranslation> extends BusinessEntity
{
    /** The primary translation language. (e.g. "en") */
    primaryLanguage!: string;
    /** The name of the foreign key for this entity in the translation (e.g. "blogPost" if this entity is a BlogPost) */
    translationForeignKeyName!: string;
    /** The translation objects for each translation language. */
    translations: BT[] = [];

    /** Get the translation object for the primary translation language. */
    get primaryTranslation()
    {
        return this.getTranslation(this.primaryLanguage);
    }

    /**
     * Get the translation object for a language.
     * 
     * @param language The language code.
     */
    getTranslation(language: string): BT | undefined
    {
        return this.translations.find(et => et.language == language);
    }

    /**
     * Get the translation object for a language. If the translation is not found, return the primary translation.
     * 
     * @param language The language code.
     */
    getTranslationWithFallback(language: string): BT | undefined
    {
        let translation = this.getTranslation(language);

        if (translation == null)
        {
            return this.getTranslation(this.primaryLanguage);
        }
        else
        {
            return translation;
        }
    }

    /**
     * Get the value of a translated field.
     * 
     * @param field The field name
     * @param language The translation language.
     * @returns The value of the translated field.
     */
    getField(field: string, language: string)
    {
        let translation = this.translations.find(et => et.language == language);
        if (translation !== undefined)
        {
            // @ts-ignore
            return translation[field];
        }
        else
        {
            return undefined;
        }
    }

    /**
     * Create a new translation object for a language.
     * 
     * @param language The language code.
     * @returns The newly created translation object.
     */
    createTranslation(language: string): BT | undefined 
    {
        // Check if translation for this language already exists
        if (this.getTranslation(language))
        {
            return this.getTranslation(language);
        }

        // We need access to the translation entity class to create a new instance
        // This can be done by accessing the manager's translationEntityClass
        // @ts-ignore - Using any here since we don't have proper typing for the manager
        let manager = (this.constructor as any).manager as EntityWithTranslationManager<BT, this>;
        if (!manager.translationEntityClass)
        {
            console.error('Translation entity class not defined in manager');
            return undefined;
        }

        // Create new translation instance
        let translation = new manager.translationEntityClass() as BT;
        translation.language = language;

        // Set foreign key to point back to this entity
        // @ts-ignore
        translation[this.translationForeignKeyName] = this;

        // Add to translations array
        this.translations.push(translation);

        return translation;
    }

    /**
     * Ensure that a default translation exists for the primary language.
     */
    ensureDefaultTranslation(): void 
    {
        if (!this.primaryLanguage) 
        {
            return;
        }

        // Check if a translation already exists for the primary language
        if (!this.getTranslation(this.primaryLanguage)) 
        {
            this.createTranslation(this.primaryLanguage);
        }
    }

    /**
     * Save the entity and all its translations.
     */
    async save()
    {
        // Make a copy of the translations array
        let translations = [...this.translations];

        // Save the entity
        let success = await super.save();
        
        // Save the translations
        if (success)
        {
            for (let translation of translations)
            {
                // Set the foreign key of the translation to point back to this entity. Eg: "blogPost" for a BlogPostTranslation
                // @ts-ignore
                translation[this.translationForeignKeyName] = this;

                success = await translation.save();
                if (!success)
                {
                    // Remove the foreign key
                    // @ts-ignore
                    delete translation[this.translationForeignKeyName];

                    break;
                }
            }
        }

        // Reload the entity along with the translations
        await this.refreshFromDB();

        return success;
    }
}

export class EntityWithTranslationManager<BT extends BusinessEntityTranslation, B extends BusinessEntityWithTranslation<BT>> extends EntityManager<B>
{
    translationEntityClass!: any;

    constructor(parameters: EntityWithTranslationManagerParameters<BT, B>)
    {
        super({
            name: parameters.name,
            createEntity: parameters.createEntity,
            fields: [
                { name: 'primaryLanguage', type: BusinessEntityFieldType.VarChar, isRequiredInput: true },
                { name: 'translations', type: BusinessEntityFieldType.Custom, isInput: false },
                ...parameters.fields ?? [],
            ],
            graphQlQueryAlias: parameters.graphQlQueryAlias
        });

        // Add the custom GraphQl fields for the translations
        let translationsField = this.fields.find(f => f.name == 'translations') as BusinessEntityField;
        translationsField.customGraphQl = `id language ${parameters.translationsCustomGraphQlFields}`;
    }

    // Override: parse the translation ID as a number
    copyFromGraphQL(entity: B, graphQlObject: any)
    {
        super.copyFromGraphQL(entity, graphQlObject);

        // parse the translation ID as a number
        entity.translations.forEach(et => et.id = parseInt(et.id as any));

        // convert translations to BusinessEntity
        entity.translations = entity.translations.map(et => this.translationEntityClass.createFromGraphQL(et));

        // add the entity object to the translations
        // @ts-ignore
        entity.translations.forEach(et => et[entity.translationForeignKeyName] = entity);
    }

    // Override: should load the translations as well
    async loadLight(filter?: BusinessEntityFilter, paging?: BusinessEntityPaging, ordering?: string[], variables?: BusinessEntityQueryVariables)
    {
        let customFields = this.buildEntityFieldsGraphQl(undefined, true, true); // skip related entities and custom GraphQL fields

        // add the translations custom fields to the custom fields
        let translationField = this.fields.find(f => f.name == 'translations') as BusinessEntityField;
        let translationsCustomFields = `
            translations {
                language
                ${translationField.customGraphQl}
            }
        `
        customFields += ` ${translationsCustomFields}`;
        
        return await this.loadCustom(customFields, filter, paging, ordering, [], variables);
    }
}

export interface EntityWithTranslationManagerParameters<BT extends BusinessEntityTranslation, B extends BusinessEntityWithTranslation<BT>> extends EntityManagerParameters<B> 
{
    /** The translated fields. */
    translationsCustomGraphQlFields?: string;
}

export default BusinessEntityWithTranslation;